import { create } from 'zustand';
import type { WeatherStation } from '../types/weatherStation';
import type { StationSearchType } from '../types/stationSearch';
import { mapSearchTypeToTagKey } from '../types/stationSearch';
import { createLogger } from '../utils/logger';
import { weatherStationAPI, APIError, ErrorType } from '../services/WeatherStationAPI';
import { useStationDataStore } from './stationDataStore';
import { useMapStore } from './mapStore';
import { useTableStore } from './tableStore';

const log = createLogger('SearchStore');

// ponytail: activeSearchMethod uses the union from AppState, not StationSearchType
// (StationSearchType is for the unified station/tag bar only).
type ActiveSearchMethod = 'stationCode' | 'coordinates' | 'country' | 'tags' | 'station' | null;

export interface SearchState {
  searchQuery: string;
  isSearching: boolean;
  searchError: string | null;
  activeSearchMethod: ActiveSearchMethod;
  searchCenter: { latitude: number; longitude: number } | null;

  /** Country search session state — stores the selected location for the current country search.
   * Requirements: 9.2 */
  countrySearchState: {
    selectedCountry: string;
    selectedState: string;
    selectedCity: string;
  } | null;

  /** GeoJSON data for country boundary overlays rendered by MapView.
   * Stored in Zustand so MapView can read it as a prop — no event dispatch needed.
   * Requirements: 6.4, 8.1, 8.3 */
  countryBoundaryLayers: any[] | null;
}

export interface SearchActions {
  setSearchQuery: (query: string) => void;
  setActiveSearchMethod: (method: ActiveSearchMethod) => void;
  searchStation: (code: string) => Promise<void>;
  searchByTags: (tagKey: string, tagValues: string[]) => Promise<void>;
  searchByCoordinates: (latitude: number, longitude: number, radius?: number) => Promise<void>;
  searchByStationTag: (searchType: StationSearchType, values: string[]) => Promise<void>;
  /** Execute a country search using pre-fetched station data.
   * Requirements: 5.1, 5.2, 5.3, 5.5, 5.6, 9.1, 9.2, 9.3, 9.4 */
  searchByCountry: (country: string, stations: WeatherStation[], state?: string, city?: string) => void;
  /** Store fetched boundary GeoJSON for MapView to render. Requirements: 6.4, 8.1 */
  setCountryBoundaryLayers: (layers: any[] | null) => void;
  clearSearch: () => void;
}

export type SearchStore = SearchState & SearchActions;

// ponytail: callbacks for cross-store error/warning display.
// Set externally by app init so searchStore stays decoupled.
let onError: ((message: string, actionLabel?: string, onAction?: () => void) => void) | undefined;
let onWarning: ((message: string) => void) | undefined;

export function setSearchErrorHandler(handler: typeof onError) { onError = handler; }
export function setSearchWarningHandler(handler: typeof onWarning) { onWarning = handler; }

export const useSearchStore = create<SearchStore>((set, get) => ({
  // --- State (defaults match appStore initialState) ---
  searchQuery: '',
  isSearching: false,
  searchError: null,
  activeSearchMethod: null,
  searchCenter: null,
  countrySearchState: null,
  countryBoundaryLayers: null,

  // --- Actions ---

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  // Task 3.1: Clear countrySearchState when switching away from 'country' (Requirements: 9.3)
  setActiveSearchMethod: (method) => {
    const updates: Partial<SearchState> = {
      activeSearchMethod: method,
      searchError: null,
    };
    // Clear country search state when switching to a non-country method
    if (method !== 'country' && get().countrySearchState !== null) {
      updates.countrySearchState = null;
      updates.countryBoundaryLayers = null;
    }
    set(updates);
  },

  /** Store fetched boundary GeoJSON for MapView to render. Requirements: 6.4, 8.1 */
  setCountryBoundaryLayers: (layers: any[] | null) => {
    set({ countryBoundaryLayers: layers });
  },

  // Search for a station by code (supports multiple codes separated by comma, space, or tab)
  searchStation: async (code) => {
    if (!code.trim()) return;

    // Parse multiple station codes (comma, space, or tab separated)
    const codes = code
      .split(/[,\s\t]+/)
      .map(c => c.trim())
      .filter(c => c.length > 0);

    if (codes.length === 0) return;

    set({
      isSearching: true,
      searchError: null,
    });

    // Cross-store: clear station data
    const stationData = useStationDataStore.getState();
    stationData.setSelectedStation(null);
    stationData.setSelectedStations([]);

    try {
      const api = weatherStationAPI;
      const validStations: WeatherStation[] = [];
      const batchSize = 50;

      for (let i = 0; i < codes.length; i += batchSize) {
        const batch = codes.slice(i, i + batchSize);
        log.debug(`Fetching batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(codes.length / batchSize)}: ${batch.length} stations`);

        try {
          const batchResults = await api.getStationsByCodes(batch);
          validStations.push(...batchResults);
        } catch (batchError) {
          log.warn(`⚠️ Batch fetch failed for ${batch.length} stations, skipping batch`);
        }

        if (i + batchSize < codes.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (validStations.length === 0) {
        throw new APIError(
          ErrorType.STATION_NOT_FOUND,
          'No stations found. Please check the station codes and try again.'
        );
      }

      log.info(`✅ Successfully fetched ${validStations.length} out of ${codes.length} stations`);

      // Cross-store: update station data
      stationData.setSelectedStation(validStations[0]);
      stationData.setSelectedStations(validStations);

      set({ isSearching: false, searchError: null });

      // Task 5.1 & 5.2: Handle single vs multiple result behavior for station code search
      // (Requirements: 1.1, 1.2, 1.3, 1.4, 9.1, 9.2, 9.3)
      if (validStations.length === 1) {
        const station = validStations[0];
        log.info(`[searchStation] Single result found: ${station.stationCode}, auto-zooming to level 12`);

        window.dispatchEvent(new CustomEvent('openStationPopup', {
          detail: {
            stationCode: station.stationCode,
            station: station,
            shouldZoom: true,
            shouldRecenter: false,
            currentZoom: 2,
            targetZoom: 12,
          }
        }));

        useMapStore.getState().selectStation(station.stationCode, { lat: station.latitude, lon: station.longitude }, 12);
      } else if (validStations.length > 1) {
        log.info(`[searchStation] Multiple results found: ${validStations.length} stations, fitting bounds`);

        window.dispatchEvent(new CustomEvent('fitStationBounds', {
          detail: { stations: validStations, padding: 50 },
        }));

        useMapStore.getState().selectStation(null);
      }
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage });
      useStationDataStore.getState().setSelectedStation(null);
      useStationDataStore.getState().setSelectedStations([]);

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchStation(code); });
      }
    }
  },

  // Search for stations by tags with tag key and values
  searchByTags: async (tagKey: string, tagValues: string[]) => {
    if (tagValues.length === 0) return;

    set({ isSearching: true, searchError: null });

    const stationData = useStationDataStore.getState();
    stationData.setSelectedStation(null);
    stationData.setSelectedStations([]);

    try {
      const api = weatherStationAPI;
      const stations = await api.getStationsByTags(tagKey, tagValues);

      if (stations.length === 0) {
        throw new APIError(
          ErrorType.STATION_NOT_FOUND,
          'No stations found for the specified tags. Please try different tags.'
        );
      }

      // Filter to only primary results for the details panel
      const primaryStations = stations.filter(s => s.isPrimaryResult !== false);

      // Cross-store: update station data
      stationData.setSelectedStation(primaryStations[0]);
      stationData.setSelectedStations(primaryStations);

      set({ isSearching: false, searchError: null });

      // Task 5.3 & 5.4: Handle single vs multiple result behavior for tag search
      if (primaryStations.length === 1) {
        const station = primaryStations[0];
        log.info(`[searchByTags] Single result found: ${station.stationCode}, auto-zooming to level 12`);

        window.dispatchEvent(new CustomEvent('openStationPopup', {
          detail: {
            stationCode: station.stationCode,
            station: station,
            shouldZoom: true,
            shouldRecenter: false,
            currentZoom: 2,
            targetZoom: 12,
          }
        }));

        useMapStore.getState().selectStation(station.stationCode, { lat: station.latitude, lon: station.longitude }, 12);
      } else if (primaryStations.length > 1) {
        log.info(`[searchByTags] Multiple results found: ${primaryStations.length} stations, fitting bounds`);

        window.dispatchEvent(new CustomEvent('fitStationBounds', {
          detail: { stations: primaryStations, padding: 50 },
        }));

        useMapStore.getState().selectStation(null);
      }
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage });

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchByTags(tagKey, tagValues); });
      }
    }
  },

  // Search for stations by coordinates (radius search)
  searchByCoordinates: async (latitude: number, longitude: number, radiusKm: number = 50) => {
    set({ isSearching: true, searchError: null, searchCenter: null });

    const stationData = useStationDataStore.getState();
    stationData.setSelectedStation(null);
    stationData.setSelectedStations([]);

    try {
      const api = weatherStationAPI;
      const stations = await api.getStationsByCoordinates(latitude, longitude, radiusKm);

      if (stations.length === 0) {
        throw new APIError(
          ErrorType.STATION_NOT_FOUND,
          `No stations found within ${radiusKm}km of the specified coordinates.`
        );
      }

      // Cross-store: update station data (don't select a single station for coordinates search)
      stationData.setSelectedStations(stations);

      set({
        isSearching: false,
        searchError: null,
        searchCenter: { latitude, longitude },
      });

      // Set initial sort for coordinates search via tableStore (Requirements: 19.1, 19.4)
      useTableStore.setState({ sortColumn: 'distance', sortDirection: 'asc' });

      // Task 5.5: Implement coordinate search initial display
      log.info(`[searchByCoordinates] Found ${stations.length} stations within ${radiusKm}km, fitting bounds`);

      window.dispatchEvent(new CustomEvent('fitStationBounds', {
        detail: {
          stations: stations,
          searchCenter: { latitude, longitude },
          padding: 50,
        }
      }));

      useMapStore.getState().selectStation(null);
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage, searchCenter: null });

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchByCoordinates(latitude, longitude, radiusKm); });
      }
    }
  },

  /**
   * Execute a country search using pre-fetched station data.
   * Requirements: 5.1, 5.2, 5.3, 5.5, 5.6, 9.1, 9.2, 9.3, 9.4
   */
  searchByCountry: (country: string, stations: WeatherStation[], state?: string, city?: string) => {
    log.debug(`🔍 [searchByCountry] Searching: country=${country}, state=${state || ''}, city=${city || ''}, stations=${stations.length}`);

    set({ isSearching: true, searchError: null });

    try {
      // Requirements: 5.1, 5.2 — filter by most specific selection
      let filtered = stations;

      if (state) {
        filtered = filtered.filter(s => s.state === state);
      }

      if (city) {
        filtered = filtered.filter(s => s.tags?.city === city);
      }

      log.info(`✅ [searchByCountry] Found ${filtered.length} stations`);

      // Cross-store: update station data
      useStationDataStore.getState().setSelectedStations(filtered);

      // Requirements: 5.3, 9.2 — store results and search state
      set({
        countrySearchState: {
          selectedCountry: country,
          selectedState: state || '',
          selectedCity: city || '',
        },
        isSearching: false,
        searchError: null,
      });

      // Dispatch fitStationBounds event if we have results
      if (filtered.length > 0) {
        window.dispatchEvent(new CustomEvent('fitStationBounds', {
          detail: { stations: filtered, padding: 50 },
        }));
      }

      // Clear map selection state
      useMapStore.getState().selectStation(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Country search failed. Please try again.';
      log.error(`❌ [searchByCountry] Error: ${errorMessage}`);

      set({ isSearching: false, searchError: errorMessage });
    }
  },

  /** Execute a unified station/tag search delegating to the appropriate API method.
   * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 6.1, 6.2, 6.3, 6.4, 6.5 */
  searchByStationTag: async (searchType: StationSearchType, values: string[]) => {
    if (values.length === 0) return;

    // Step 1: Clear previous state — set isSearching, clear errors and results
    set({ isSearching: true, searchError: null });

    const stationData = useStationDataStore.getState();
    stationData.setSelectedStation(null);
    stationData.setSelectedStations([]);

    try {
      const api = weatherStationAPI;
      const validStations: WeatherStation[] = [];

      if (searchType === 'stationCode') {
        // Requirements: 3.1, 3.4 — Station code search: batch in groups of 50
        const batchSize = 50;

        for (let i = 0; i < values.length; i += batchSize) {
          const batch = values.slice(i, i + batchSize);
          log.debug(`[searchByStationTag] Fetching station code batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(values.length / batchSize)}: ${batch.length} codes`);

          try {
            const batchResults = await api.getStationsByCodes(batch);
            validStations.push(...batchResults);
          } catch {
            log.warn(`⚠️ [searchByStationTag] Batch fetch failed, skipping`);
          }

          if (i + batchSize < values.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      } else {
        // Requirements: 3.2, 3.3 — Tag-based search: delegate to getStationsByTags
        const tagKey = mapSearchTypeToTagKey(searchType);
        log.debug(`[searchByStationTag] Searching by tag: key="${tagKey}", values=${values.join(',')}`);

        const stations = await api.getStationsByTags(tagKey, values);
        validStations.push(...stations);
      }

      // Requirements: 6.1 — No results found
      if (validStations.length === 0) {
        throw new APIError(
          ErrorType.STATION_NOT_FOUND,
          'No stations found. Please check your search values and try again.'
        );
      }

      log.info(`✅ [searchByStationTag] Found ${validStations.length} stations`);

      // Cross-store: update station data
      stationData.setSelectedStation(validStations[0]);
      stationData.setSelectedStations(validStations);

      set({ isSearching: false, searchError: null });

      // Requirements: 3.7 — Dispatch fitStationBounds event to fit map viewport
      window.dispatchEvent(new CustomEvent('fitStationBounds', {
        detail: { stations: validStations, padding: 50 },
      }));
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage });

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchByStationTag(searchType, values); });
      }
    }
  },

  // Clear search results and reset search state
  // Task 3.1: Also clear countrySearchState (Requirements: 9.3)
  clearSearch: () => {
    set({
      searchQuery: '',
      searchError: null,
      searchCenter: null,
      countrySearchState: null,
      countryBoundaryLayers: null,
    });

    // Cross-store: clear station data and table highlight
    useStationDataStore.getState().setSelectedStation(null);
    useStationDataStore.getState().setSelectedStations([]);
    useTableStore.getState().highlightRow(null);
  },
}));
